import { useMemo, useRef, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, User, Plus, ExternalLink } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useOpportunity,
  useProposals,
  useJobs,
  useInvoices,
} from "@/hooks/use-api";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { PropertyHero } from "@/components/v2/property-hero";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { JobPhotoGallery } from "@/components/v2/media/job-photo-gallery";
import { PhotoPreviewStrip } from "@/components/v2/media/photo-preview-strip";
import { OpportunityLifecycleTimeline } from "@/components/v2/timeline/opportunity-lifecycle-timeline";
import { deriveOpportunityLifecycle } from "@/lib/lifecycle-events";
import { useScrollSpy } from "@/hooks/use-scroll-spy";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

// Opportunity stages per ADV-812 enum: lead → appointment → proposal → closed_won/lost.
// Job-side states (in_progress/completed) live on the Job entity now.
const STAGE_ORDER = [
  "lead",
  "appointment",
  "proposal",
  "closed_won",
  "closed_lost",
] as const;

const STAGE_LABELS: Record<string, string> = {
  lead: "Lead",
  appointment: "Appointment",
  proposal: "Proposal",
  closed_won: "Closed Won",
  closed_lost: "Lost",
};

const STAGE_COLORS: Record<string, string> = {
  lead: "var(--color-text-lo)",
  appointment: "var(--color-warning)",
  proposal: "var(--color-accent)",
  closed_won: "var(--color-success)",
  closed_lost: "var(--color-danger)",
};

const CURRENT_FOCUS: Record<
  string,
  { heading: string; body: string; action: string }
> = {
  lead: {
    heading: "Qualify this lead",
    body: "Schedule an appointment to assess the property and gather scope.",
    action: "Schedule Appointment",
  },
  appointment: {
    heading: "Appointment scheduled",
    body: "Prepare for the site visit and document scope requirements.",
    action: "Add Notes",
  },
  proposal: {
    heading: "Proposal in review",
    body: "Follow up with the customer on any outstanding questions.",
    action: "Send Follow-up",
  },
  closed_won: {
    heading: "Deal closed",
    body: "Schedule production and assign the crew to get the job started.",
    action: "View Jobs",
  },
  closed_lost: {
    heading: "Deal lost",
    body: "Document the reason for loss to improve future pipeline accuracy.",
    action: "Add Note",
  },
};

function getStageLabel(stage: string): string {
  return (
    STAGE_LABELS[stage] ??
    stage.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())
  );
}

function getDealTypeLabel(dealType: string): string {
  return dealType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

// Right rail key/value row
function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

// Right rail section wrapper
function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

// Stage row in the stages section
function StageRow({
  stageKey,
  currentStage,
}: {
  stageKey: string;
  currentStage: string;
}) {
  const stageIdx = STAGE_ORDER.indexOf(
    stageKey as (typeof STAGE_ORDER)[number],
  );
  const currentIdx = STAGE_ORDER.indexOf(
    currentStage as (typeof STAGE_ORDER)[number],
  );

  const isPast = stageIdx < currentIdx;
  const isActive = stageKey === currentStage;
  const isFuture = stageIdx > currentIdx && !isActive;

  const diamondColor = isActive
    ? (STAGE_COLORS[stageKey] ?? "var(--color-accent)")
    : isPast
      ? "var(--color-text-lo)"
      : "var(--color-border)";

  return (
    <div className="flex items-center gap-3">
      {/* Diamond bullet */}
      <div
        className="h-2.5 w-2.5 shrink-0 rotate-45"
        style={{
          backgroundColor: isActive || isPast ? diamondColor : "transparent",
          border: `1.5px solid ${diamondColor}`,
        }}
        aria-hidden="true"
      />
      <span
        className={
          isActive
            ? "typo-body font-medium text-[color:var(--color-text-hi)]"
            : isFuture
              ? "typo-body text-[color:var(--color-text-lo)]"
              : "typo-body text-[color:var(--color-text-md)]"
        }
      >
        {getStageLabel(stageKey)}
      </span>
      {isActive && (
        <Badge
          variant="secondary"
          className="ml-auto text-[10px] h-4 px-1.5 uppercase tracking-wide"
        >
          Current
        </Badge>
      )}
    </div>
  );
}

const proposalColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "proposal_number",
    header: "Proposal #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.proposal_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "final_price",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.final_price as number),
  },
  {
    accessorKey: "submit_date",
    header: "Date",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.submit_date as string)}
      </span>
    ),
  },
];

const jobColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "job_number",
    header: "Job #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.job_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "scheduled_date",
    header: "Scheduled",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.scheduled_date as string)}
      </span>
    ),
  },
  {
    accessorKey: "total_revenue",
    header: "Revenue",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.total_revenue as number),
  },
];

const invoiceColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "invoice_number",
    header: "Invoice #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.invoice_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "total_amount",
    header: "Total",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.total_amount as number),
  },
  {
    accessorKey: "due_date",
    header: "Due",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.due_date as string)}
      </span>
    ),
  },
];

export default function V2OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const { data, isLoading, isError } = useOpportunity(id!);

  // Linked child entities (deal_id FK per ADV-463)
  const { data: proposalsData, isLoading: proposalsLoading } = useProposals({
    deal_id: id,
    limit: 50,
  });
  const { data: jobsData, isLoading: jobsLoading } = useJobs({
    deal_id: id,
    limit: 50,
  });
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({
    deal_id: id,
    limit: 50,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const opportunity = (data as any)?.opportunity as Row | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const location = (opportunity?.location as Row | null) ?? null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proposals = ((proposalsData as any)?.proposals ?? []) as Row[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jobs = ((jobsData as any)?.jobs ?? []) as Row[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const invoices = ((invoicesData as any)?.invoices ?? []) as Row[];

  const totalRecords = proposals.length + jobs.length + invoices.length;

  // ADV-145: derive the opportunity lifecycle feed from the already-fetched
  // child records (slice 1 — no new endpoint). The "moment" axis (decision T2).
  // Computed inline — the inputs are freshly-derived arrays each render, so a
  // useMemo over them would never hit; the derivation is cheap on these sizes.
  const lifecycleEvents = deriveOpportunityLifecycle(
    opportunity,
    proposals,
    jobs,
    invoices,
  );

  // ADV-1345: time-correlated scroll-spy between the lifecycle rail and the
  // activity feed. The feed is NOT its own scroll container (the app shell's
  // <main> scrolls), so `feedRef` only addresses the rows — the rows are
  // scrolled into view against whatever ancestor actually scrolls. The
  // milestone projection is memoized on a stable id+timestamp signature so the
  // observer is not re-created every render (lifecycleEvents is a fresh array
  // each render — see note above).
  const feedRef = useRef<HTMLDivElement>(null);
  const lifecycleSignature = lifecycleEvents
    .map((e) => `${e.id}@${e.timestamp}`)
    .join("|");
  const lifecycleMilestones = useMemo(
    () => lifecycleEvents.map((e) => ({ id: e.id, timestamp: e.timestamp })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signature captures the milestone identity; lifecycleEvents is a fresh array each render
    [lifecycleSignature],
  );
  const { activeId, scrollTo } = useScrollSpy({
    milestones: lifecycleMilestones,
    feedRef,
    // The feed lives in the lazily-mounted Activity tab — arm the spy only once
    // that panel is on-screen (else feedRef.current is null and the observer
    // never attaches). ADV-1345 Codex P2.
    active: activeTab === "activity",
  });

  const title = useMemo(() => {
    if (!opportunity) return `Opportunity ${id?.slice(0, 8) ?? ""}`;
    const name = opportunity.name as string | undefined;
    const number = opportunity.deal_number as string | undefined;
    return name || number || `Opportunity ${id?.slice(0, 8)}`;
  }, [opportunity, id]);

  const breadcrumbLabel = useMemo(() => {
    if (!opportunity) return id ?? "...";
    return (
      (opportunity.deal_number as string) ||
      (opportunity.name as string) ||
      title
    );
  }, [opportunity, id, title]);

  const stage = (opportunity?.stage as string) || "lead";
  const currentFocus = CURRENT_FOCUS[stage] ?? CURRENT_FOCUS.lead;

  // Build address string from location
  const address = useMemo(() => {
    if (!location) return null;
    const formatted = location.formatted_address as string | null;
    if (formatted) return formatted;
    const parts = [
      location.street_line_1,
      location.city,
      location.state,
    ].filter(Boolean);
    return parts.length > 0 ? (parts.join(", ") as string) : null;
  }, [location]);

  const pageTitle = useMemo(() => {
    const customerName = opportunity?.customer_name as string | undefined;
    if (customerName && address) return `${customerName} — ${address}`;
    if (customerName) return customerName;
    if (address) return address;
    return title;
  }, [opportunity, address, title]);

  const dealType = opportunity?.deal_type
    ? getDealTypeLabel(opportunity.deal_type as string)
    : null;

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading opportunity"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || (!isLoading && !opportunity)) {
    const title = isError
      ? "Failed to load opportunity"
      : "Opportunity not found";
    const description = isError
      ? "Something went wrong. Please try again."
      : "This opportunity may have been deleted.";
    return (
      <>
        <Breadcrumb className="mb-4 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/opportunities"
                className="text-[color:var(--color-text-lo)]"
              >
                Opportunities
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{id ?? "..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <EmptyState title={title} description={description} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Breadcrumbs + per-page actions cluster */}
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb className="typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/opportunities"
                className="text-[color:var(--color-text-lo)]"
              >
                Opportunities
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{breadcrumbLabel}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <DetailPageActions entityLabel="Opportunity" />
        </div>
      </div>

      {/* 3-tab shell */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col"
      >
        {/* Tab bar */}
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
          <TabsTrigger
            value="photos"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Photos
          </TabsTrigger>
          <TabsTrigger
            value="records"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Records
            {totalRecords > 0 && (
              <Badge
                variant="secondary"
                className="ml-1.5 h-5 min-w-[20px] px-1.5 text-[11px]"
              >
                {String(totalRecords)}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0">
          <div className="flex gap-6 pt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 space-y-5">
              {/* Property hero strip */}
              <PropertyHero
                address={address ?? ""}
                lat={(location?.latitude as number | null) ?? undefined}
                lon={(location?.longitude as number | null) ?? undefined}
              />

              {/* Title block */}
              <div>
                <h1 className="typo-heading text-[color:var(--color-text-hi)]">
                  {pageTitle}
                </h1>
                {dealType && (
                  <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                    {dealType}
                    {opportunity?.description
                      ? ` · ${opportunity.description as string}`
                      : ""}
                  </p>
                )}
              </div>

              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                <span
                  className="inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label"
                  style={{
                    borderColor: STAGE_COLORS[stage] ?? "var(--color-border)",
                    color: STAGE_COLORS[stage] ?? "var(--color-text-md)",
                  }}
                >
                  {getStageLabel(stage)}
                </span>
                {dealType && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{dealType}</span>
                  </>
                )}
                {(opportunity?.proposal_value as number) > 0 && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span className="tabular-nums">
                      {formatCurrency(opportunity!.proposal_value as number)}
                    </span>
                  </>
                )}
                {opportunity?.lead_date != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{formatDate(opportunity.lead_date as string)}</span>
                  </>
                )}
              </div>

              {/* Current Focus card */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
                <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
                  Current Focus
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="typo-body font-semibold text-[color:var(--color-text-hi)]">
                      {currentFocus.heading}
                    </div>
                    <p className="typo-body text-[color:var(--color-text-md)] mt-0.5">
                      {currentFocus.body}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0">
                    {currentFocus.action}
                  </Button>
                </div>
              </div>

              {/* Customer row */}
              {opportunity?.customer_name != null && (
                <div className="flex items-center gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-elevated)]">
                    <User className="h-4 w-4 text-[color:var(--color-text-md)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="typo-body font-medium text-[color:var(--color-text-hi)] truncate">
                      {opportunity.customer_name as string}
                    </div>
                    {opportunity.customer_email != null && (
                      <div className="typo-label text-[color:var(--color-text-md)] truncate">
                        {opportunity.customer_email as string}
                      </div>
                    )}
                  </div>
                  {opportunity.contact_id != null && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 gap-1.5 text-[color:var(--color-text-md)]"
                      onClick={() =>
                        navigate(
                          `/customers/${opportunity.contact_id as string}`,
                        )
                      }
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      View
                    </Button>
                  )}
                </div>
              )}

              {/* Resources row (stub) */}
              <div className="flex items-center gap-2">
                <span className="typo-label text-[color:var(--color-text-lo)]">
                  Resources
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 text-[color:var(--color-text-lo)] text-xs px-2"
                >
                  <Plus className="h-3 w-3" />
                  Add link or document
                </Button>
              </div>

              {/* Stages section */}
              <div className="space-y-2">
                {STAGE_ORDER.map((s) => (
                  <StageRow
                    key={s}
                    stageKey={s as string}
                    currentStage={stage}
                  />
                ))}
              </div>

              {/* Photos preview strip — rolls up across the opportunity's
                  jobs/proposals; deep-links to the Photos tab; renders nothing
                  until there are photos (§I3.1, §7) */}
              <PhotoPreviewStrip
                entityType="opportunity"
                entityId={id!}
                onViewAll={() => setActiveTab("photos")}
              />
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow label="Stage" value={getStageLabel(stage)} />
                <InfoRow label="Type" value={dealType} />
                <InfoRow
                  label="Owner"
                  value={opportunity?.assigned_rep_name as string | null}
                />
                <InfoRow
                  label="Customer"
                  value={opportunity?.customer_name as string | null}
                />
                <InfoRow
                  label="Number"
                  value={opportunity?.deal_number as string | null}
                />
                <InfoRow
                  label="Lead date"
                  value={
                    opportunity?.lead_date
                      ? formatDate(opportunity.lead_date as string)
                      : null
                  }
                />
                <InfoRow
                  label="Sold date"
                  value={
                    opportunity?.sold_date
                      ? formatDate(opportunity.sold_date as string)
                      : null
                  }
                />
                <InfoRow
                  label="Created"
                  value={
                    opportunity?.created_at
                      ? formatDate(opportunity.created_at as string)
                      : null
                  }
                />
              </RailSection>

              {/* Progress */}
              {((opportunity?.proposal_value as number) > 0 ||
                (opportunity?.job_revenue as number) > 0 ||
                (opportunity?.invoiced_amount as number) > 0) && (
                <RailSection title="Progress">
                  <InfoRow
                    label="Quoted"
                    value={
                      (opportunity?.proposal_value as number) > 0
                        ? formatCurrency(opportunity!.proposal_value as number)
                        : null
                    }
                  />
                  <InfoRow
                    label="Earned"
                    value={
                      (opportunity?.job_revenue as number) > 0
                        ? formatCurrency(opportunity!.job_revenue as number)
                        : null
                    }
                  />
                  <InfoRow
                    label="Billed"
                    value={
                      (opportunity?.invoiced_amount as number) > 0
                        ? formatCurrency(opportunity!.invoiced_amount as number)
                        : null
                    }
                  />
                </RailSection>
              )}

              {/* Activity preview — server-side rollup across the opportunity
                  + its child jobs/proposals/appointments/invoices */}
              <RailSection title="Activity">
                <EntityActivityPreview
                  opportunityId={id!}
                  onSeeAll={() => setActiveTab("activity")}
                />
              </RailSection>
            </div>
          </div>
        </TabsContent>

        {/* ── Activity ── lifecycle timeline (moment axis) + activity feed.
            ADV-1345: the rail and feed are time-correlated — clicking a rail
            milestone scrolls the feed to that moment, and as the page scrolls
            the rail highlights the milestone the viewport is in. The rail is
            made sticky so it stays in view while the feed scrolls past (the
            page, not the feed, is the scroll container). */}
        <TabsContent value="activity" className="mt-0 pt-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            <section className="lg:w-[340px] lg:shrink-0 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
              <h3 className="mb-3 typo-label text-[color:var(--color-text-md)]">
                Lifecycle
              </h3>
              <OpportunityLifecycleTimeline
                events={lifecycleEvents}
                activeId={activeId}
                onSelect={(event) => scrollTo(event.timestamp)}
              />
            </section>
            <section className="min-w-0 flex-1 lg:border-l lg:border-[color:var(--color-border)] lg:pl-8">
              <h3 className="mb-3 typo-label text-[color:var(--color-text-md)]">
                Activity
              </h3>
              <div ref={feedRef}>
                <EntityActivityFeed
                  opportunityId={id!}
                  emptyDescription="Messages, calls, and notes across this opportunity, its proposals, jobs, and invoices appear here once the first contact is logged."
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* ── Photos ── rolls up across the opportunity's jobs/proposals */}
        <TabsContent value="photos" className="mt-0 pt-6">
          <JobPhotoGallery entityType="opportunity" entityId={id!} />
        </TabsContent>

        {/* ── Records ── */}
        <TabsContent value="records" className="mt-0 pt-6 space-y-8">
          {/* Proposals */}
          <div>
            <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
              Proposals
              {proposals.length > 0 && (
                <span className="ml-2 typo-label text-[color:var(--color-text-lo)]">
                  {proposals.length}
                </span>
              )}
            </h2>
            <DataTable
              columns={proposalColumns}
              data={proposals}
              isLoading={proposalsLoading}
              onRowClick={(row) => navigate(`/proposals/${row.id as string}`)}
              emptyMessage="No proposals yet"
              emptyDescription="Proposals linked to this opportunity will appear here."
            />
          </div>

          {/* Jobs */}
          <div>
            <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
              Jobs
              {jobs.length > 0 && (
                <span className="ml-2 typo-label text-[color:var(--color-text-lo)]">
                  {jobs.length}
                </span>
              )}
            </h2>
            <DataTable
              columns={jobColumns}
              data={jobs}
              isLoading={jobsLoading}
              onRowClick={(row) => navigate(`/jobs/${row.id as string}`)}
              emptyMessage="No jobs yet"
              emptyDescription="Jobs linked to this opportunity will appear here."
            />
          </div>

          {/* Invoices */}
          <div>
            <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
              Invoices
              {invoices.length > 0 && (
                <span className="ml-2 typo-label text-[color:var(--color-text-lo)]">
                  {invoices.length}
                </span>
              )}
            </h2>
            <DataTable
              columns={invoiceColumns}
              data={invoices}
              isLoading={invoicesLoading}
              onRowClick={(row) => navigate(`/invoices/${row.id as string}`)}
              emptyMessage="No invoices yet"
              emptyDescription="Invoices linked to this opportunity will appear here."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
